import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { House, Users, Briefcase, Receipt, Warning, SignOut, Storefront, IdentificationBadge, ClipboardText, Robot, HouseSimple, Wallet, ShoppingCart, Rocket, TrendUp, TrendDown, Buildings, Bank, CurrencyInr, WhatsappLogo, Gear } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import api from "@/lib/api";
import GlobalSearch from "@/components/GlobalSearch";
import DigitalSetuLogo from "@/components/DigitalSetuLogo";
import { BRAND } from "@/lib/brand";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, testid: "sidebar-nav-dashboard", end: true },
  { to: "/customers", label: "Customers", icon: Users, testid: "sidebar-nav-customers" },
  { to: "/applications", label: "Applications", icon: Briefcase, testid: "sidebar-nav-applications" },
  { to: "/billing", label: "Billing", icon: Receipt, testid: "sidebar-nav-billing" },
  { to: "/pos", label: "POS Billing", icon: ShoppingCart, testid: "sidebar-nav-pos", badge: "NEW" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "sidebar-nav-payments" },
  { to: "/complaints", label: "Complaints", icon: Warning, testid: "sidebar-nav-complaints" },
  { to: "/marketing", label: "Marketing & Leads", icon: TrendUp, testid: "sidebar-nav-marketing", badge: "NEW" },
  { to: "/expenses", label: "Expenses", icon: TrendDown, testid: "sidebar-nav-expenses", badge: "NEW" },
  { to: "/collections", label: "Collections", icon: Wallet, testid: "sidebar-nav-collections", badge: "NEW" },
  { to: "/gov-finance", label: "Gov Settlement", icon: Bank, testid: "sidebar-nav-govfinance", badge: "NEW" },
  { to: "/hr", label: "HR & Payroll", icon: IdentificationBadge, testid: "sidebar-nav-hr", badge: "NEW" },
  { to: "/reports", label: "Reports", icon: ClipboardText, testid: "sidebar-nav-reports", badge: "NEW" },
  { to: "/rent-agreements", label: "Rent Agreements", icon: HouseSimple, testid: "sidebar-nav-rent" },
  { to: "/ai-drafts", label: "AI Draft", icon: Robot, testid: "sidebar-nav-ai", badge: "AI" },
  { to: "/print-center", label: "Print Center", icon: IdentificationBadge, testid: "sidebar-nav-print", badge: "NEW" },
  { to: "/payment-center", label: "Payment Center", icon: CurrencyInr, testid: "sidebar-nav-payments", badge: "NEW" },
  { to: "/gov-portals", label: "Gov Portals", icon: Buildings, testid: "sidebar-nav-portals", badge: "NEW" },
  { to: "/whatsapp-center", label: "WhatsApp Center", icon: WhatsappLogo, testid: "sidebar-nav-wa", badge: "NEW" },
  { to: "/tracking", label: "Tracking Dashboard", icon: Rocket, testid: "sidebar-nav-tracking", badge: "NEW" },
  { to: "/photo-enhancer", label: "AI Photo Enhancer", icon: House, testid: "sidebar-nav-photo-enhancer", badge: "PRO" },
  { to: "/photo-studio", label: "Photo & Scan", icon: House, testid: "sidebar-nav-photo", badge: "AI" },
];

const ADMIN_NAV = [
  { to: "/settings", label: "Settings", icon: Gear, testid: "sidebar-nav-settings", badge: "NEW" },
  { to: "/staff", label: "Staff & Roles", icon: IdentificationBadge, testid: "sidebar-nav-staff" },
  { to: "/service-rates", label: "Service Rates", icon: Bank, testid: "sidebar-nav-rates", badge: "NEW" },
  { to: "/branches-admin", label: "Branches", icon: Buildings, testid: "sidebar-nav-branches", badge: "NEW" },
  { to: "/audit-logs", label: "Audit Logs", icon: ClipboardText, testid: "sidebar-nav-audit" },
];

const SUPER_ADMIN_NAV = [
  { to: "/go-live", label: "Launch Portal · System Recovery", icon: Rocket, testid: "sidebar-nav-golive", tone: "text-red-300 hover:text-red-100" },
];

const ADMIN_ROLES = new Set(["admin", "super_admin", "branch_admin"]);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && ADMIN_ROLES.has(user.role);
  const isSuper = user?.role === "super_admin";
  const [branches, setBranches] = React.useState([]);
  const [activeBranch, setActiveBranch] = React.useState(() => localStorage.getItem("csc_active_branch") || "");

  React.useEffect(() => {
    if (!isSuper) return;
    // Fetch branches WITHOUT the interceptor injecting branch_id (bypass by explicit param)
    api.get("/branches", { params: { branch_id: "" } })
      .then((r) => setBranches(r.data || []))
      .catch(() => setBranches([]));
  }, [isSuper]);

  const changeBranch = (val) => {
    setActiveBranch(val);
    if (val) localStorage.setItem("csc_active_branch", val);
    else localStorage.removeItem("csc_active_branch");
    // Reload so every already-mounted page refetches with the new scope
    window.location.reload();
  };

  const [searchModalOpen, setSearchModalOpen] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K" || e.key === "/")) {
        e.preventDefault();
        setSearchModalOpen(true);
      } else if (e.key === "Escape") {
        setSearchModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-[260px] shrink-0 bg-slate-900 text-slate-100 flex flex-col fixed inset-y-0 left-0" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
              <DigitalSetuLogo size={26} variant="mono" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{BRAND.sub}</div>
              <div className="font-semibold text-lg -mt-0.5" style={{ fontFamily: "var(--font-heading)" }}>{BRAND.name}</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
                  isActive ? "bg-slate-800 text-white nav-link-active" : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
                }`
              }
            >
              <n.icon size={18} weight="regular" />
              <span className="flex-1">{n.label}</span>
              {n.badge && <span className="text-[9px] font-bold bg-orange-600 text-white px-1.5 py-0.5 rounded uppercase tracking-wider">{n.badge}</span>}
            </NavLink>
          ))}

          {isAdmin && (
            <>
              <div className="mt-6 mb-2 px-4 text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Admin</div>
              {ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to} to={n.to} data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-slate-800 text-white nav-link-active" : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
                    }`
                  }
                >
                  <n.icon size={18} weight="regular" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </>
          )}

          {user?.role === "super_admin" && (
            <>
              <div className="mt-6 mb-2 px-4 text-[10px] uppercase tracking-[0.25em] font-semibold text-red-400/70">Super Admin</div>
              {SUPER_ADMIN_NAV.map((n) => (
                <NavLink
                  key={n.to} to={n.to} data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-red-900/40 text-red-100 nav-link-active" : `${n.tone || "text-slate-300"} hover:bg-red-900/30`
                    }`
                  }
                >
                  <n.icon size={18} weight="regular" />
                  <span>{n.label}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-slate-800">
          {isSuper && (
            <div className="mb-3" data-testid="branch-switcher-wrap">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
                <Buildings size={9} weight="fill" /> Branch Scope
              </div>
              <select
                value={activeBranch}
                onChange={(e) => changeBranch(e.target.value)}
                data-testid="branch-switcher"
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:border-cyan-500"
              >
                <option value="">My Branch (default)</option>
                <option value="all">All Branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.code} · {b.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-orange-600/20 border border-orange-600/40 flex items-center justify-center text-orange-400 font-semibold">
              {(user?.name || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || "Admin"}</div>
              <div className="text-[10px] text-slate-400 truncate uppercase tracking-widest">{user?.role?.replace(/_/g, " ")}</div>
              {user?.branch && (
                <div className="text-[10px] text-cyan-400 truncate flex items-center gap-1 mt-0.5" data-testid="user-branch-badge">
                  <Buildings size={9} weight="fill" /> {isSuper && activeBranch === "all" ? "All Branches" : isSuper && activeBranch ? (branches.find(b => b.id === activeBranch)?.name || user.branch.name) : `${user.branch.code} · ${user.branch.name}`}
                  {!isSuper && <span className="ml-1 text-[8px] text-slate-500">(locked)</span>}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center justify-center gap-2 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm transition-colors"
          >
            <SignOut size={16} /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-[260px] min-h-screen">
        {user && <BranchContextBar user={user} isSuper={isSuper} branches={branches} activeBranch={activeBranch} onChange={changeBranch} />}
        <Outlet />
      </main>

      {searchModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={() => setSearchModalOpen(false)} data-testid="global-search-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl">
            <GlobalSearch autoFocus onClose={() => setSearchModalOpen(false)} />
            <div className="text-center text-xs text-slate-300 mt-3">Press <kbd className="px-1 py-0.5 bg-slate-800 rounded text-slate-100 mx-1">Esc</kbd> to close · <kbd className="px-1 py-0.5 bg-slate-800 rounded text-slate-100 mx-1">Ctrl/⌘ K</kbd> from anywhere to reopen</div>
          </div>
        </div>
      )}

      <Toaster position="bottom-right" richColors />
    </div>
  );
}

// ----------------------------------------------------------------
// Persistent branch/role context bar — visible on every ERP page
// ----------------------------------------------------------------
const ROLE_LABEL = {
  super_admin: "Super Admin",
  branch_admin: "Branch Admin",
  admin: "Admin",
  operator: "Operator",
  accountant: "Accountant",
  customer_support: "Customer Support",
  reception: "Reception",
  doc_officer: "Documents",
  complaint_manager: "Complaints",
  service_manager: "Services",
  inventory_manager: "Inventory",
  viewer: "Viewer",
  view_only: "View Only",
};
const ROLE_TONE = {
  super_admin: "bg-red-100 text-red-800",
  branch_admin: "bg-blue-100 text-blue-800",
  admin: "bg-blue-100 text-blue-800",
  operator: "bg-emerald-100 text-emerald-800",
  accountant: "bg-purple-100 text-purple-800",
  customer_support: "bg-cyan-100 text-cyan-800",
  reception: "bg-pink-100 text-pink-800",
  doc_officer: "bg-amber-100 text-amber-800",
  complaint_manager: "bg-red-100 text-red-800",
  service_manager: "bg-indigo-100 text-indigo-800",
  inventory_manager: "bg-teal-100 text-teal-800",
  viewer: "bg-slate-100 text-slate-700",
  view_only: "bg-slate-100 text-slate-700",
};

function BranchContextBar({ user, isSuper, branches, activeBranch, onChange }) {
  const showing = isSuper
    ? (activeBranch ? (branches.find((b) => b.id === activeBranch)?.name || "Selected Branch") : "🌐 All Branches")
    : (user.branch_name || user.branch || "My Branch");
  const roleTone = ROLE_TONE[user.role] || "bg-slate-100 text-slate-700";
  return (
    <div className="sticky top-0 z-20 h-10 px-4 lg:px-6 bg-white/85 backdrop-blur border-b border-slate-200 flex items-center gap-3 text-xs" data-testid="branch-context-bar">
      <div className="flex items-center gap-1.5" data-testid="branch-context-branch">
        <Buildings size={13} className={isSuper && !activeBranch ? "text-orange-500" : "text-[#1e3a8a]"} weight="fill" />
        <span className="text-slate-500 hidden sm:inline">Branch:</span>
        <span className="font-semibold text-slate-900">{showing}</span>
        {isSuper && (
          <select
            value={activeBranch}
            onChange={(e) => onChange(e.target.value)}
            className="ml-2 h-6 rounded-md bg-slate-50 border border-slate-200 text-[11px] font-medium text-slate-700 focus:outline-none focus:border-orange-400 px-1"
            data-testid="branch-context-select"
          >
            <option value="">🌐 All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>
      <span className="text-slate-300">·</span>
      <div className="flex items-center gap-1.5" data-testid="branch-context-user">
        <span className="text-slate-500 hidden sm:inline">You:</span>
        <span className="font-semibold text-slate-900 truncate max-w-[160px]">{user.name || user.email}</span>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-semibold text-[10px] uppercase tracking-wider ${roleTone}`}>
          {ROLE_LABEL[user.role] || user.role || "Guest"}
        </span>
      </div>
      <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-500">
        {!isSuper && (
          <span className="hidden md:inline-flex items-center gap-1 text-emerald-700">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Data isolated to your branch
          </span>
        )}
        <kbd className="hidden md:inline px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-slate-500">Ctrl+K</kbd>
      </div>
    </div>
  );
}
